import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { adminDeleteCurrency, adminListCurrencies, adminSaveCurrency, listCurrencies } from '../../api/currencies';
import { formatMoney, setCurrencies } from '../../shared/currency/currencyStore';
import AdminShell, { useAdminAccess } from './AdminShell';

// Every ISO 4217 currency the browser knows, for the "Add currency" picker.
const currencyNames = new Intl.DisplayNames(['en'], { type: 'currency' });
const isoCurrencies = (Intl.supportedValuesOf?.('currency') || []).map((code) => [code, currencyNames.of(code)]);

const SAMPLE_INR = 10000;
const toRow = (c) => ({ ...c, inrPerUnit: String(c.inrPerUnit), markupPercent: String(c.markupPercent), sortOrder: String(c.sortOrder), dirty: false });
const preview = (row) => {
  const rate = Number(row.inrPerUnit);
  if (!(rate > 0)) return '—';
  return formatMoney((SAMPLE_INR / rate) * (1 + (Number(row.markupPercent) || 0) / 100), row.code);
};

export default function AdminCurrenciesPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState({ code: '', inrPerUnit: '', markupPercent: '2' });

  const load = useCallback(async () => {
    try {
      setRows((await adminListCurrencies(auth.accessToken)).map(toRow));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    if (hasAccess) load();
  }, [hasAccess, load]);

  // Keep the header picker in step with what admins just changed.
  const refreshSite = () => listCurrencies().then(setCurrencies).catch(() => {});

  const run = async (key, action, done) => {
    setBusy(key);
    setError('');
    setMessage('');
    try {
      await action();
      await load();
      refreshSite();
      setMessage(done);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(null);
    }
  };

  const update = (code, field) => (event) => {
    const { type, checked, value } = event.target;
    setRows((list) => list.map((r) => (r.code === code ? { ...r, [field]: type === 'checkbox' ? checked : value, dirty: true } : r)));
  };

  const body = (r) => ({
    name: r.name,
    inrPerUnit: Number(r.inrPerUnit),
    markupPercent: Number(r.markupPercent) || 0,
    isEnabled: r.isEnabled,
    sortOrder: Number(r.sortOrder) || 0,
  });

  const save = (r) => run(r.code, () => adminSaveCurrency(auth.accessToken, r.code, body(r)), `${r.code} saved.`);

  const remove = (r) => {
    if (!window.confirm(`Remove ${r.code}? Customers who chose it will see prices in INR.`)) return;
    run(r.code, () => adminDeleteCurrency(auth.accessToken, r.code), `${r.code} removed.`);
  };

  const add = (event) => {
    event.preventDefault();
    const code = adding.code;
    const name = currencyNames.of(code);
    run('add', async () => {
      await adminSaveCurrency(auth.accessToken, code, {
        name, inrPerUnit: Number(adding.inrPerUnit), markupPercent: Number(adding.markupPercent) || 0,
        isEnabled: false, sortOrder: rows.length,
      });
      setAdding({ code: '', inrPerUnit: '', markupPercent: '2' });
    }, `${code} added. Check the rate, then tick “Offered” and save to show it to customers.`);
  };

  const available = rows ? isoCurrencies.filter(([code]) => !rows.some((r) => r.code === code)) : [];

  return (
    <AdminShell
      title="Currencies"
      subtitle="Customers can view prices in these currencies. Bookings, promo codes and fees stay in INR; payment is always taken in INR."
    >
      {error && <p className="api-message error" role="alert">{error}</p>}
      {message && <p className="api-message success" role="status">{message}</p>}
      {!rows && !error && <p role="status">Loading currencies…</p>}
      {rows && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table fee-table">
              <thead>
                <tr><th>Currency</th><th>Offered</th><th>1 unit = ₹</th><th>Markup %</th><th>Order</th><th>₹10,000 shows as</th><th>Updated</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.code}>
                    <td><b>{r.code}</b><div className="muted small">{r.name}</div></td>
                    {r.isBase ? (
                      <td colSpan="4" className="fee-example">Booking currency — always offered at rate 1</td>
                    ) : (
                      <>
                        <td><input type="checkbox" aria-label={`Offer ${r.code}`} checked={r.isEnabled} onChange={update(r.code, 'isEnabled')} /></td>
                        <td><input type="number" aria-label={`${r.code} rate in INR`} min="0.000001" step="any" required value={r.inrPerUnit} onChange={update(r.code, 'inrPerUnit')} /></td>
                        <td><input type="number" aria-label={`${r.code} markup`} min="0" max="25" step="0.01" value={r.markupPercent} onChange={update(r.code, 'markupPercent')} /></td>
                        <td><input type="number" aria-label={`${r.code} order`} min="0" max="1000" step="1" value={r.sortOrder} onChange={update(r.code, 'sortOrder')} /></td>
                      </>
                    )}
                    <td>{r.isBase ? '₹10,000' : preview(r)}</td>
                    <td className="muted small">{new Date(r.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td className="admin-row-actions">
                      {!r.isBase && (
                        <>
                          <button type="button" className="btn btn-primary btn-sm" disabled={!r.dirty || busy === r.code} onClick={() => save(r)}>Save</button>
                          <button type="button" className="btn btn-outline btn-sm danger" disabled={busy === r.code} onClick={() => remove(r)}>Remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form className="panel admin-section currency-add" onSubmit={add}>
            <h3>Add a currency</h3>
            <p className="muted small">Added currencies start hidden so you can check the rate first. The markup is added to converted prices to cover exchange-rate movement.</p>
            <div className="form-grid three">
              <label className="field"><span className="field-label">Currency</span>
                <select required value={adding.code} onChange={(e) => setAdding({ ...adding, code: e.target.value })}>
                  <option value="" disabled>Select a currency</option>
                  {available.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
                </select>
              </label>
              <label className="field"><span className="field-label">1 unit = ₹</span>
                <input required type="number" min="0.000001" step="any" value={adding.inrPerUnit} onChange={(e) => setAdding({ ...adding, inrPerUnit: e.target.value })} placeholder="e.g. 84.10" />
              </label>
              <label className="field"><span className="field-label">Markup %</span>
                <input type="number" min="0" max="25" step="0.01" value={adding.markupPercent} onChange={(e) => setAdding({ ...adding, markupPercent: e.target.value })} />
              </label>
            </div>
            <div><button className="btn btn-primary" disabled={busy === 'add'}>{busy === 'add' ? 'Adding…' : 'Add currency'}</button></div>
          </form>
        </>
      )}
    </AdminShell>
  );
}
